// components/EditableCell.tsx
import React, { useState, useRef, useEffect } from 'react';
import { TableCell } from '@nextui-org/react';

interface EditableCellProps {
  initialValue: string | number | null | undefined;
  onSave: (value: string | number) => void;
  type?: 'text' | 'number';
  className?: string;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  initialValue,
  onSave,
  type = 'text',
  className = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<string | number>(() => {
    if (initialValue === null || initialValue === undefined) return '';
    return initialValue;
  });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (value !== initialValue) {
      onSave(type === 'number' ? Number(value) || 0 : value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (value !== initialValue) {
        onSave(type === 'number' ? Number(value) || 0 : value);
      }
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setValue(initialValue ?? '');
    }
  };

  const displayValue = React.useMemo(() => {
    if (type === 'number' && typeof value === 'number') {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return value;
  }, [value, type]);

  return (
    <div className={className}>
      {isEditing ? (
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => setValue(type === 'number' ? Number(e.target.value) || 0 : e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full bg-background border border-blue-400 rounded px-2 py-1 focus:outline-none"
        />
      ) : (
        <div 
          onDoubleClick={handleDoubleClick}
          className="cursor-pointer hover:bg-default-100 px-2 py-1 rounded"
        >
          {displayValue}
        </div>
      )}
    </div>
  );
};